import { useState, FormEvent } from 'react';
import { AxiosError } from 'axios';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../api/userApi';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { usePageTitle } from '../hooks/usePageTitle';
import { clearSession } from '../utils/storage';
import type { ApiError } from '../types/auth';

export function AccountPage() {
  usePageTitle('Account — Hawkes Bridge');
  const { user, updateUser } = useAuth();
  const { toasts, showSuccess, showError, removeToast } = useToast();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // v1.4: Clear History confirmation dialog state (T120-T122)
  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false);
  const [isClearingHistory, setIsClearingHistory] = useState(false);

  const handleProfileUpdate = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setIsLoading(true);

    try {
      const updatedUser = await userApi.updateProfile({
        firstName: firstName !== user?.firstName ? firstName : undefined,
        lastName: lastName !== user?.lastName ? lastName : undefined,
      });

      updateUser(updatedUser);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>;
      setMessage({
        type: 'error',
        text: axiosError.response?.data?.error?.message || 'Failed to update profile.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }

    setIsLoading(true);

    try {
      await userApi.updateProfile({
        currentPassword,
        newPassword,
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>;
      setMessage({
        type: 'error',
        text: axiosError.response?.data?.error?.message || 'Failed to change password.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // T120-T122: Handle Clear Problem History
  // T129 (BUG-001 FIX): Also clear localStorage session state
  const handleClearHistory = async () => {
    setIsClearingHistory(true);
    try {
      const response = await userApi.clearProblemHistory();
      // BUG-001 FIX: Clear the persisted session state to prevent lingering session
      // data from re-adding records when user continues practicing
      await clearSession();
      setShowClearHistoryDialog(false);
      showSuccess(response.message || `History cleared (${response.deletedCount} problems)`);
    } catch {
      setShowClearHistoryDialog(false);
      showError('Failed to clear problem history');
    } finally {
      setIsClearingHistory(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-ink mb-8">Account Settings</h1>

      {message && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-card rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-xl font-semibold text-ink mb-4">Profile Information</h2>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="First Name"
              type="text"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label="Last Name"
              type="text"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          <Input
            label="Email"
            type="email"
            name="email"
            value={user?.email || ''}
            disabled
            className="bg-cream"
          />

          <Button type="submit" isLoading={isLoading}>
            Update Profile
          </Button>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-card rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-xl font-semibold text-ink mb-4">Change Password</h2>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            name="currentPassword"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />

          <Input
            label="New Password"
            type="password"
            name="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />

          <Input
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />

          <Button type="submit" isLoading={isLoading}>
            Change Password
          </Button>
        </form>
      </div>

      {/* T120: Clear Problem History (v1.4) */}
      <div className="bg-card rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-ink mb-4">Problem History</h2>
        <p className="text-ink-soft mb-4">
          Clear your problem history to start fresh. This marks every question as unseen again
          across all practice pages — Bidding Practice, Precision bidding, Competitive bidding,
          and Counting.
        </p>
        <button
          type="button"
          onClick={() => setShowClearHistoryDialog(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium transition-colors"
        >
          Clear Problem History
        </button>
      </div>

      {/* T121: Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showClearHistoryDialog}
        title="Clear Problem History"
        message="Are you sure you want to clear all your problem history? This action cannot be undone. Every question across all practice pages will be marked as unseen."
        confirmLabel="Clear History"
        cancelLabel="Cancel"
        confirmVariant="danger"
        isLoading={isClearingHistory}
        onConfirm={handleClearHistory}
        onCancel={() => setShowClearHistoryDialog(false)}
      />

      {/* T122: Toast notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
